'use client'

import { useState, useEffect } from 'react'
import { Search, Calendar, Clock, Tag, BookOpen } from 'lucide-react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import SmartImage from '@/components/ui/SmartImage'
import { siteImages } from '@/data/siteImages'

const avatarFallback = (name: string) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Author')}&background=0ea5e9&color=fff`

interface BlogPost {
  _id: string; title: string; slug: string; excerpt: string; content: string;
  author: { name: string; avatar: string; role: string };
  category: string; tags: string[]; publishedAt: string; readTime: number;
  featured: boolean; image: string; status: 'draft' | 'published';
}

interface BlogCategory {
  _id: string; name: string; slug: string; description?: string;
  color: string; postCount: number; isActive: boolean;
}

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [categories, setCategories] = useState<BlogCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    const loadBlogData = async () => {
      try {
        const [postsRes, categoriesRes] = await Promise.all([
          fetch('/api/blog-posts'), fetch('/api/blog-categories')
        ])
        if (postsRes.ok) {
          const postsData = await postsRes.json()
          setPosts(postsData.filter((post: BlogPost) => post.status === 'published'))
        }
        if (categoriesRes.ok) setCategories(await categoriesRes.json())
      } catch (error) { console.error('Error loading blog:', error) }
      finally { setLoading(false) }
    }
    loadBlogData()
  }, [])

  const filteredPosts = posts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    return matchesCategory && matchesSearch
  })

  const featuredPosts = posts.filter(post => post.featured).slice(0, 2)
  const allCategories = ['All', ...categories.map(cat => cat.name)]

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-500 text-sm">Loading articles...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/30 to-white pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(56,189,248,0.08),transparent)] pointer-events-none" />
        <div className="container mx-auto px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-300 text-sm font-medium mb-4">
              <BookOpen className="w-4 h-4" /> Blog & Insights
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-sky-200 bg-clip-text text-transparent">
              Tech Insights & Updates
            </h1>
            <p className="text-slate-400 mb-8">
              Stay ahead with the latest trends and best practices from our technology experts
            </p>
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text" placeholder="Search articles..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white/5 border border-sky-200/20 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all text-sm"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-4 gap-10">
          {/* Main */}
          <div className="lg:col-span-3">
            {/* Featured */}
            {featuredPosts.length > 0 && selectedCategory === 'All' && !searchQuery && (
              <div className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-5">Featured</h2>
                <div className="grid md:grid-cols-2 gap-5">
                  {featuredPosts.map((post, i) => (
                    <motion.div key={post._id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                      <Link href={`/blog/${post.slug}`} className="group block">
                        <div className="relative overflow-hidden rounded-2xl mb-4 h-52">
                          <SmartImage src={post.image || siteImages.blog.fallbackCover} fallbackSrc={siteImages.blog.fallbackCover}
                            alt={post.title} fill sizes="(max-width: 768px) 100vw, 50vw"
                            className="object-cover group-hover:scale-105 transition-transform duration-500" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                          <span className="absolute bottom-3 left-3 px-2.5 py-1 bg-sky-500 text-white text-xs font-medium rounded-lg">Featured</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                          <span className="flex items-center gap-1"><Calendar size={12} />{new Date(post.publishedAt).toLocaleDateString()}</span>
                          <span className="flex items-center gap-1"><Clock size={12} />{post.readTime} min</span>
                        </div>
                        <h3 className="font-bold text-slate-900 group-hover:text-sky-600 transition-colors mb-1.5">{post.title}</h3>
                        <p className="text-sm text-slate-500 line-clamp-2">{post.excerpt}</p>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Category Filter */}
            <div className="mb-6 flex flex-wrap gap-2">
              {allCategories.map((category) => (
                <button key={category} onClick={() => setSelectedCategory(category)}
                  className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-md shadow-sky-200'
                      : 'bg-white text-slate-600 border border-sky-100 hover:border-sky-300 hover:text-sky-600'
                  }`}>
                  {category}
                  {category !== 'All' && <span className="ml-1 text-xs opacity-75">({categories.find(c => c.name === category)?.postCount || 0})</span>}
                </button>
              ))}
            </div>

            {/* Posts */}
            <div className="space-y-5">
              {filteredPosts.length > 0 ? filteredPosts.map((post, i) => (
                <motion.article key={post._id} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} viewport={{ once: true }}>
                  <Link href={`/blog/${post.slug}`} className="group block bg-white rounded-2xl shadow-sm border border-sky-100/50 hover:shadow-lg hover:shadow-sky-100/50 hover:border-sky-200 transition-all duration-300 overflow-hidden">
                    <div className="md:flex">
                      <div className="md:w-1/3 relative h-44 md:h-auto md:min-h-[180px]">
                        <SmartImage src={post.image || siteImages.blog.fallbackCover} fallbackSrc={siteImages.blog.fallbackCover}
                          alt={post.title} fill sizes="(max-width: 768px) 100vw, 33vw"
                          className="object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="md:w-2/3 p-6">
                        <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                          <span className="px-2.5 py-0.5 bg-sky-50 text-sky-600 rounded-md font-medium">{post.category}</span>
                          <span className="flex items-center gap-1"><Calendar size={12} />{new Date(post.publishedAt).toLocaleDateString()}</span>
                          <span className="flex items-center gap-1"><Clock size={12} />{post.readTime} min</span>
                        </div>
                        <h2 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-sky-600 transition-colors">{post.title}</h2>
                        <p className="text-sm text-slate-500 line-clamp-2 mb-4">{post.excerpt}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="relative w-7 h-7 rounded-full overflow-hidden">
                              <SmartImage src={post.author?.avatar || avatarFallback(post.author?.name)} fallbackSrc={avatarFallback(post.author?.name)}
                                alt={post.author?.name || 'Author'} fill sizes="28px" className="object-cover" />
                            </div>
                            <span className="text-xs font-medium text-slate-700">{post.author?.name}</span>
                          </div>
                          <div className="flex gap-1.5">
                            {post.tags.slice(0, 2).map(tag => (
                              <span key={tag} className="flex items-center gap-0.5 text-[10px] text-slate-400"><Tag size={10} />{tag}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              )) : (
                <div className="text-center py-16">
                  <h3 className="font-semibold text-slate-900 mb-1">No articles found</h3>
                  <p className="text-sm text-slate-500">Try adjusting your search or filter.</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Recent */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-sky-100/50">
                <h3 className="font-bold text-slate-900 mb-4 text-sm">Recent Posts</h3>
                <div className="space-y-3">
                  {posts.slice(0, 5).map((post) => (
                    <Link key={post._id} href={`/blog/${post.slug}`} className="group block">
                      <h4 className="text-xs font-medium text-slate-700 group-hover:text-sky-600 transition-colors mb-1 line-clamp-2">{post.title}</h4>
                      <div className="flex items-center text-[10px] text-slate-400 gap-1"><Calendar size={10} />{new Date(post.publishedAt).toLocaleDateString()}</div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Categories */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-sky-100/50">
                <h3 className="font-bold text-slate-900 mb-4 text-sm">Categories</h3>
                <div className="space-y-1">
                  {categories.map((cat) => (
                    <button key={cat._id} onClick={() => setSelectedCategory(cat.name)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                        selectedCategory === cat.name ? 'bg-sky-50 text-sky-600 font-medium' : 'text-slate-600 hover:bg-sky-50/50'
                      }`}>
                      <div className="flex justify-between"><span>{cat.name}</span><span className="text-slate-400">({cat.postCount})</span></div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Newsletter */}
              <div className="bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl p-5 text-white">
                <h3 className="font-bold mb-2 text-sm">Stay Updated</h3>
                <p className="text-sky-100 text-xs mb-3">Get latest insights in your inbox</p>
                <input type="email" placeholder="Your email" className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-sky-200 text-xs focus:ring-2 focus:ring-white/30 mb-2" />
                <button className="w-full bg-white text-sky-600 font-semibold py-2 rounded-lg text-xs hover:bg-sky-50 transition-colors">Subscribe</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
